/* iter-51z — Admin Users panel.
 *
 * Lets a Pro-Writer-scoped admin manage other admin accounts:
 *   • view every admin and their scopes
 *   • create a new admin (defaults to bookstore-only)
 *   • toggle pro_writer / bookstore scopes per user
 *   • delete a custom admin (seeded accounts are protected)
 */
import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Plus, Trash2, Shield, Store, RotateCw, KeyRound, Lock, Activity, LogIn, UserPlus, SlidersHorizontal, UserMinus, AlertTriangle, GraduationCap, PenTool, TestTube2, BookText, FileText, ShieldCheck, Film, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import { timeAgo } from '@/lib/format';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OnlineLoomUsers } from './OnlineLoomUsers';

const SCOPE_LABELS = {
  pro_writer: { icon: Shield,         label: 'The Loom admin',   help: 'Access /admin' },
  bookstore:  { icon: Store,          label: 'Bookstore admin',    help: 'Access /bookstore/admin' },
  // iter55q — Premier Studio human-services roles.
  coach:      { icon: GraduationCap,  label: 'Coach',              help: 'Access the coaching queue only' },
  editor:     { icon: PenTool,        label: 'Publishing Editor',  help: 'Access review, publication workbench, print proofs, genre tree, and Indie Reader sync' },
  // Feb-2026 · Fine-grained finance scopes.
  ledger_entry: { icon: BookText,     label: 'Ledger clerk',       help: 'Post journal entries only — no AP approvals' },
  ap_ar:        { icon: FileText,     label: 'AR / AP clerk',      help: 'Prepare AR/AP entries; AP entries route to approval' },
  ap_approver:  { icon: ShieldCheck,  label: 'AP approver (CFO)',  help: 'Review & approve AP disbursements' },
  compliance:   { icon: ShieldCheck,  label: 'Compliance',         help: 'Upload / view compliance & license documents' },
  marketing_media: { icon: Film,      label: 'Marketing & Media',  help: 'View training reels and media production status' },
};

// iter-51z-activity — visual styling per audit kind.
const EVENT_META = {
  admin_user_created:        { icon: UserPlus,           tone: 'text-emerald-700', label: 'created'         },
  admin_user_scopes_changed: { icon: SlidersHorizontal,  tone: 'text-sky-700',     label: 'scopes changed'  },
  admin_user_deleted:        { icon: UserMinus,          tone: 'text-rose-700',    label: 'deleted'         },
  admin_user_password_set:   { icon: Eye,                tone: 'text-indigo-700',  label: 'password set'    },
  admin_login_success:       { icon: LogIn,              tone: 'text-muted-foreground', label: 'signed in'  },
  admin_login_failure:       { icon: AlertTriangle,      tone: 'text-amber-700',   label: 'login failed'    },
};

function ActivityFeed() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);  // forces re-render every 30s for live timeAgo

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.adminUsersActivity(20);
      setEvents(r.events || []);
    } catch {
      // Silent; the parent panel surfaces its own errors. The feed is
      // nice-to-have, never blocking.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);
  useEffect(() => {
    const t = window.setInterval(() => setTick(x => x + 1), 30 * 1000);
    return () => window.clearInterval(t);
  }, []);

  return (
    <div className="rounded-md border border-border bg-card shadow-panel" data-testid="admin-users-activity">
      <div className="flex items-center justify-between border-b border-border bg-panel-header/40 px-3 py-2">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold">
          <Activity className="h-3.5 w-3.5 text-primary" /> Recent admin activity
        </div>
        <Button size="sm" variant="ghost" onClick={reload} disabled={loading} className="h-7 gap-1.5 text-[11px]" data-testid="admin-users-activity-refresh">
          <RotateCw className={'h-3 w-3 ' + (loading ? 'animate-spin' : '')} /> Refresh
        </Button>
      </div>
      <ul className="divide-y divide-border max-h-72 overflow-y-auto">
        {events.map(ev => {
          const meta = EVENT_META[ev.kind] || { icon: Activity, tone: 'text-muted-foreground', label: ev.kind };
          const Icon = meta.icon;
          return (
            <li key={ev.id} className="flex items-start gap-2 px-3 py-2 text-[11.5px]"
                data-testid={`admin-activity-row-${ev.kind}`}>
              <Icon className={'mt-0.5 h-3 w-3 shrink-0 ' + meta.tone} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-1 leading-tight">
                  <span className="font-mono-stat font-semibold">{ev.actor || '—'}</span>
                  <span className={'text-[10.5px] ' + meta.tone}>{meta.label}</span>
                  {ev.target && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-mono-stat">{ev.target}</span>
                    </>
                  )}
                </div>
                {ev.kind === 'admin_user_scopes_changed' && ev.detail && (
                  <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                    <code className="rounded-sm bg-muted px-1">[{(ev.detail.from || []).join(', ') || '·'}]</code>
                    {' → '}
                    <code className="rounded-sm bg-muted px-1">[{(ev.detail.to || []).join(', ') || '·'}]</code>
                  </div>
                )}
                {ev.kind === 'admin_user_created' && ev.detail?.scopes && (
                  <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                    scopes <code className="rounded-sm bg-muted px-1">[{ev.detail.scopes.join(', ') || '·'}]</code>
                  </div>
                )}
                {(ev.kind === 'admin_login_success' || ev.kind === 'admin_login_failure') && ev.detail?.ip && (
                  <div className="mt-0.5 text-[10.5px] text-muted-foreground">
                    from <code className="rounded-sm bg-muted px-1">{ev.detail.ip}</code>
                  </div>
                )}
              </div>
              <span className="shrink-0 text-[10.5px] text-muted-foreground" title={ev.at}
                    data-tick={tick}>
                {timeAgo(ev.at)}
              </span>
            </li>
          );
        })}
        {!events.length && !loading && (
          <li className="px-3 py-4 text-center text-[11.5px] text-muted-foreground">
            No admin activity yet.
          </li>
        )}
      </ul>
    </div>
  );
}

function QaTrainingUserCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = async () => {
    try { setStatus(await adminApi.qaUserStatus()); }
    catch { setStatus({ exists: false }); }
  };
  useEffect(() => { reload(); }, []);
  const toggle = async (next) => {
    setBusy(true);
    try {
      await adminApi.qaUserToggle(next);
      toast.success(next ? 'QA user enabled — admin-sandbox@invalid.crownnrose.local can sign in.'
                          : 'QA user disabled — login will now 403.');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed.');
    } finally { setBusy(false); }
  };
  if (!status) return null;
  const enabled = !!status.enabled;
  return (
    <div className="rounded-md border border-border bg-card p-4 shadow-panel"
         data-testid="qa-user-card">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-none items-center justify-center rounded-sm bg-primary/10 text-primary">
          <TestTube2 className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold">QA training user</div>
          <div className="text-[11.5px] text-muted-foreground">
            <code className="rounded-sm bg-muted px-1">admin-sandbox@invalid.crownnrose.local</code>{' '}
            — Premier tier, admin permissions, wipes every tour marker on each
            login. <b>Disable before public release.</b>
          </div>
          {!status.exists && (
            <div className="mt-1 text-[11px] text-amber-800 dark:text-amber-300">
              User not seeded yet. Restart the backend or run the seed script.
            </div>
          )}
          {status.exists && status.last_login_at && (
            <div className="mt-1 text-[10.5px] text-muted-foreground">
              Last login: {status.last_login_at}
            </div>
          )}
        </div>
        <div className="flex-none">
          <label className="inline-flex items-center gap-2 text-[11.5px]"
                 data-testid="qa-user-toggle-label">
            <Switch checked={enabled}
                    disabled={busy || !status.exists}
                    onCheckedChange={toggle}
                    data-testid="qa-user-toggle" />
            <span className={enabled ? 'font-semibold text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}>
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}

// iter-click-fix — Oct-2026. Direct, visible password set for any admin
// account. The value is plain-text (unmasked) as the operator types it —
// no guessing what was actually saved, and no email-reset-link detour
// (which left operators signing back in against the wrong defaulted
// account). Still bcrypt-hashed server-side; nothing plaintext persists.
function SetPasswordDialog({ username }) {
  const [open, setOpen]   = useState(false);
  const [pw, setPw]       = useState('');
  const [busy, setBusy]   = useState(false);

  const save = async () => {
    if (pw.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    setBusy(true);
    try {
      await adminApi.adminUsersSetPassword(username, pw);
      toast.success(`Password set for ${username}.`);
      setPw(''); setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set password.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPw(''); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-sky-700" data-testid={`admin-user-set-pw-${username}`}>
          <Eye className="h-3 w-3" /> Set password
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`admin-user-set-pw-dialog-${username}`}>
        <DialogHeader>
          <DialogTitle>Set password for {username}</DialogTitle>
          <DialogDescription>
            Takes effect immediately — {username}'s existing sessions are signed out. The value stays visible here while you type it so there's no ambiguity about what was saved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-[11.5px]">New password (visible)</Label>
          <Input
            type="text"
            autoComplete="off"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Minimum 8 characters"
            className="font-mono-stat"
            data-testid={`admin-user-set-pw-input-${username}`}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" data-testid={`admin-user-set-pw-cancel-${username}`}>Cancel</Button>
          </DialogClose>
          <Button onClick={save} disabled={busy || pw.length < 8} data-testid={`admin-user-set-pw-save-${username}`}>
            {busy ? 'Saving…' : 'Save password'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminUsersPanel() {
  const [rows, setRows]     = useState([]);
  const [valid, setValid]   = useState(['pro_writer', 'bookstore']);
  const [seeded, setSeeded] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', proWriter: false });
  const [onlineCount, setOnlineCount] = useState(0);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminApi.adminUsersList();
      setRows(r.users || []);
      setValid(r.valid_scopes || ['pro_writer', 'bookstore']);
      setSeeded(r.seeded_usernames || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load admin users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const toggleScope = async (row, scope) => {
    const current = new Set(row.scopes || []);
    if (current.has(scope)) current.delete(scope); else current.add(scope);
    try {
      await adminApi.adminUsersSetScopes(row.username, Array.from(current));
      toast.success(`Updated scopes for ${row.username}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed.');
    }
  };

  const create = async () => {
    if (!newUser.username.trim() || newUser.password.length < 8) {
      toast.error('Username and ≥8-char password required.');
      return;
    }
    setCreating(true);
    try {
      const scopes = ['bookstore'];
      if (newUser.proWriter) scopes.push('pro_writer');
      await adminApi.adminUsersCreate({
        username: newUser.username.trim(),
        password: newUser.password,
        scopes,
      });
      toast.success(`Created ${newUser.username.trim()}.`);
      setNewUser({ username: '', password: '', proWriter: false });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed.');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (username) => {
    try {
      await adminApi.adminUsersDelete(username);
      toast.success(`Deleted ${username}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  // iter-admin-password-rotate — One-click reset for seeded accounts.
  const [resetBusy, setResetBusy] = useState(null);       // username currently rotating
  const rotatePassword = async (username) => {
    setResetBusy(username);
    try {
      const r = await adminApi.adminUsersResetPassword(username);
      toast.success(r.emailed
        ? `Reset link emailed for ${username}.`
        : `Reset link requested for ${username}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed.');
    } finally { setResetBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="admin-admin-users-panel">
      <PanelHeader
        title="Admin Users"
        subtitle="Manage admin accounts and their access scopes. Publishing Editors can review manuscripts, approve publication gates, print proofs, maintain genre trees, and sync approved titles to Indie Reader."
        onRefresh={reload}
      />
      <div className="rounded-sm border border-primary/25 bg-primary/5 px-3 py-2 text-[11px] text-muted-foreground" data-testid="admin-publishing-editor-scope-description">
        <strong className="text-foreground">Publishing Editor scope:</strong> grants the review queue, publication gates, print proofs, hierarchical genre editor, and approved Indie Reader sync. Assign or remove it with the Editor switch below.
      </div>

      <Tabs defaultValue="accounts" className="space-y-4" data-testid="admin-users-section-tabs">
        <TabsList data-testid="admin-users-tabs-list">
          <TabsTrigger value="accounts" data-testid="admin-users-accounts-tab">Admin accounts</TabsTrigger>
          <TabsTrigger value="online" className="gap-2" data-testid="admin-users-online-tab">
            Online now
            <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800" data-testid="admin-users-online-count">{onlineCount}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="accounts" className="m-0 space-y-4">

      {/* iter-p2-r — QA training-user toggle */}
      <QaTrainingUserCard />

      {/* Create form */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center gap-1.5 text-[12.5px] font-semibold">
          <Plus className="h-3.5 w-3.5 text-primary" /> Create a new admin
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto_auto]">
          <div>
            <Label className="text-[11px]">Username</Label>
            <Input
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              placeholder="StoreManager"
              className="h-8 text-[12.5px]"
              data-testid="admin-new-user-username"
            />
          </div>
          <div>
            <Label className="text-[11px]">Password (≥ 8 chars)</Label>
            <Input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder="hunter2hunter2"
              className="h-8 text-[12.5px]"
              data-testid="admin-new-user-password"
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-1.5 text-[11.5px]">
              <Switch
                checked={newUser.proWriter}
                onCheckedChange={(v) => setNewUser({ ...newUser, proWriter: v })}
                data-testid="admin-new-user-prowriter"
              />
              The Loom access
            </label>
          </div>
          <div className="flex items-end">
            <Button
              size="sm"
              onClick={create}
              disabled={creating}
              className="h-8 gap-1.5"
              data-testid="admin-new-user-create"
            >
              <Plus className="h-3.5 w-3.5" /> Create
            </Button>
          </div>
        </div>
        <p className="mt-2 text-[10.5px] text-muted-foreground">
          Default scope is <code className="rounded-sm bg-muted px-1">bookstore</code> only. Toggle The Loom access at creation OR later in the table below.
        </p>
      </div>

      {/* Users table */}
      <div className="rounded-md border border-border bg-card shadow-panel">
        <div className="flex items-center justify-between border-b border-border bg-panel-header/40 px-3 py-2">
          <div className="text-[12px] font-semibold">All admin accounts</div>
          <Button size="sm" variant="ghost" onClick={reload} disabled={loading} className="h-7 gap-1.5 text-[11px]" data-testid="admin-users-table-refresh">
            <RotateCw className={'h-3 w-3 ' + (loading ? 'animate-spin' : '')} /> Refresh
          </Button>
        </div>
        <table className="w-full text-[12px]">
          <thead className="bg-panel-header/30 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Username</th>
              {valid.map(scope => (
                <th key={scope} className="px-3 py-2 text-center">
                  <span className="inline-flex items-center gap-1" data-testid={`admin-scope-header-${scope}`}>
                    {React.createElement(SCOPE_LABELS[scope]?.icon || KeyRound, { className: 'h-3 w-3' })}
                    {SCOPE_LABELS[scope]?.label || scope}
                  </span>
                </th>
              ))}
              <th className="px-3 py-2 text-right">Last login</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isSeeded = seeded.includes(row.username);
              return (
                <tr key={row.username} className="border-t border-border hover:bg-secondary/40" data-testid={`admin-user-row-${row.username}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono-stat text-[12px]">{row.username}</div>
                    {isSeeded && (
                      <span className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                        <Lock className="h-2.5 w-2.5" /> seeded (protected)
                      </span>
                    )}
                  </td>
                  {valid.map(scope => {
                    const checked = (row.scopes || []).includes(scope);
                    return (
                      <td key={scope} className="px-3 py-2 text-center">
                        <Switch
                          checked={checked}
                          onCheckedChange={() => toggleScope(row, scope)}
                          data-testid={`admin-user-scope-${row.username}-${scope}`}
                        />
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right text-[11px] text-muted-foreground">
                    {row.last_login_at ? new Date(row.last_login_at).toLocaleString() : 'never'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                    <SetPasswordDialog username={row.username} />
                    {isSeeded && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={resetBusy === row.username}
                            className="h-7 gap-1 text-amber-700"
                            data-testid={`admin-user-reset-pw-${row.username}`}
                          >
                            <KeyRound className="h-3 w-3" /> Reset password
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Send reset link for {row.username}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Sends a one-time link to the configured admin recovery mailbox. The current password remains valid until the link is completed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => rotatePassword(row.username)}
                              data-testid={`admin-user-reset-pw-confirm-${row.username}`}
                            >
                              Send reset link
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    {!isSeeded && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7 gap-1 text-destructive" data-testid={`admin-user-delete-${row.username}`}>
                            <Trash2 className="h-3 w-3" /> Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete admin account {row.username}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently removes the credential row. The user will lose access immediately. Audit history is preserved.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(row.username)} data-testid={`admin-user-delete-confirm-${row.username}`}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={4 + valid.length} className="px-3 py-6 text-center text-[12px] text-muted-foreground">No admin users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ActivityFeed />

        </TabsContent>
        <TabsContent value="online" forceMount className="m-0 data-[state=inactive]:hidden">
          <OnlineLoomUsers onCountChange={setOnlineCount} />
        </TabsContent>
      </Tabs>

    </div>
  );
}
